import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import logo from "../assets/images/logo.png";
import { getProfileImage } from "../core/common/api";
import { hasPermission } from "../core/common/adminPermissions";
import AppImage from "./AppImage";
import Swal from "sweetalert2";

type HeaderProps = {
  onMenuClick: () => void;
};

const getGreeting = (): string => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};

const Header = ({ onMenuClick }: HeaderProps) => {
  const [profileData, setProfileData] = useState<any>(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const { logout } = useAuth();
  const profileImage = getProfileImage(profileData);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const loadProfile = () => {
      try {
        const stored = localStorage.getItem("adminProfile");
        setProfileData(stored ? JSON.parse(stored) : null);
      } catch {
        setProfileData(null);
      }
    };

    loadProfile();
    window.addEventListener("adminProfile:updated", loadProfile);
    return () => window.removeEventListener("adminProfile:updated", loadProfile);
  }, []);

  useEffect(() => {
    const focusSearch = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener("keydown", focusSearch);
    return () => window.removeEventListener("keydown", focusSearch);
  }, []);

  // Close profile dropdown on outside click
  useEffect(() => {
    if (!showProfileMenu) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target as Node)
      ) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showProfileMenu]);

  const handleLogout = async () => {
    const confirmation = await Swal.fire({
      title: "Log out?",
      text: "You will need to sign in again to access the admin panel.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Log out",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#1e3a6e",
    });

    if (!confirmation.isConfirmed) return;

    setShowProfileMenu(false);
    await logout();
    navigate("/");
  };

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const query = search.trim();
    if (!query) return;

    const lower = query.toLowerCase();
    const params = new URLSearchParams({ search: query });

    if (lower.includes("order") || lower.startsWith("#")) {
      navigate(`/orders?${params}`);
    } else if (lower.includes("history") || lower.includes("working") || lower.includes("upline")) {
      navigate(`/income-history?${params}`);
    } else if (lower.includes("customer") || lower.includes("user")) {
      navigate(`/users?${params}`);
    } else if (lower.includes("coupon") || lower.includes("discount")) {
      navigate(`/coupons?${params}`);
    } else if (lower.includes("categor")) {
      navigate(`/categories?${params}`);
    } else {
      navigate(`/products?${params}`);
    }

    setSearch("");
  };

  return (
    <header className="app-header">
      <div className="header-left">
        <button
          type="button"
          onClick={onMenuClick}
          className="menu-button"
          aria-label="Toggle sidebar"
        >
          ☰
        </button>
        <span className="mobile-header-logo">
          <img src={logo} alt="Riseway" />
        </span>

        <div className="topbar-welcome">
          <span className="topbar-greeting">{getGreeting()},</span>
          <span className="topbar-name">{profileData?.name}</span>
        </div>
      </div>

      <form className="global-search" onSubmit={handleSearch}>
        <span>🔎</span>
        <input
          ref={searchInputRef}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search for orders, products, user..."
        />
        <kbd>⌘K</kbd>
      </form>

      <div className="header-actions">
        {(profileData?.role === "superadmin" ||
          hasPermission("notifications")) && (
          <NavLink
            to="/notifications"
            className={({ isActive }) =>
              `header-icon-button ${isActive ? "active" : ""}`
            }
            aria-label="Notifications"
            title="Notifications"
          >
            <Bell size={20} />
          </NavLink>
        )}
        <div className="profile-menu-wrap" ref={profileMenuRef}>
          <button
            type="button"
            onClick={() => setShowProfileMenu((current) => !current)}
            className="profile-chip"
            aria-expanded={showProfileMenu}
            aria-haspopup="menu"
          >
            <AppImage
              src={profileImage}
              alt={profileData?.name || "Admin"}
              crossOrigin="anonymous"
            />
            <div>
              <strong>{profileData?.name || "Admin"}</strong>
              <small>{profileData?.role || "Admin"}</small>
            </div>
            <i>⌄</i>
          </button>

          {showProfileMenu && (
            <div className="profile-dropdown" role="menu">
              <button
                type="button"
                onClick={() => {
                  setShowProfileMenu(false);
                  navigate("/profile");
                }}
                role="menuitem"
              >
                <span>👤</span>
                Profile
              </button>
              <button type="button" onClick={handleLogout} role="menuitem">
                <span>🚪</span>
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
